import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../api/dates";
import { useJourney, useRecordMilestone, type Journey, type StageState } from "../../api/workflow";
import { daysOver, milestoneLabel, ownerLabel } from "./labels";

/** One job's way through the stages, loaded for the site in the address. */
export function JourneyCard({ siteId }: { siteId: number }) {
  const journey = useJourney(siteId);
  if (journey.isPending) {
    return (
      <section className="card journey-card" aria-label="Journey">
        <p className="app-loading">Loading the journey…</p>
      </section>
    );
  }
  if (journey.isError) {
    return (
      <section className="card journey-card" aria-label="Journey">
        <p className="notice bad">The journey could not be loaded: {journey.error.message}</p>
      </section>
    );
  }
  return <JourneyView journey={journey.data} />;
}

/** Shown when no job is picked and nothing is overdue to pick first. */
export function NoJourney() {
  return (
    <section className="card journey-card" aria-label="Journey">
      <p className="section-label">Journey</p>
      <p className="empty-state">Nothing is overdue. Pick a job from its site to see its journey here.</p>
    </section>
  );
}

export function JourneyView({ journey }: { journey: Journey }) {
  return (
    <section className="card journey-card" aria-labelledby="journey-site">
      <div className="journey-head">
        <p className="section-label">Journey</p>
        <h2 id="journey-site">
          <Link to={`/sites/${journey.site.id}`}>{journey.site.name}</Link>
        </h2>
      </div>
      <ol className="journey-steps" aria-label="Stages of this job">
        {journey.states.map((state) => (
          <JourneyStep key={state.stage} state={state} />
        ))}
      </ol>
      <RecordMilestone journey={journey} />
    </section>
  );
}

type StepKind = "done" | "skipped" | "late" | "open" | "pending";

function kindOf(state: StageState): StepKind {
  if (state.ended !== null) {
    return "done";
  }
  if (state.skipped) {
    return "skipped";
  }
  if (state.started !== null) {
    return (state.days_over ?? 0) > 0 ? "late" : "open";
  }
  return "pending";
}

function metaOf(state: StageState, kind: StepKind): string {
  const owner = ownerLabel(state.owner);
  switch (kind) {
    case "done":
      return `Done ${formatDate(state.ended)} · ${owner}`;
    case "skipped":
      // The work moved past it and nobody recorded it: a missing record, not
      // a job still waiting here. See `StageState.skipped` on the server.
      return `Passed without a record · ${owner}`;
    case "late":
    case "open":
      return state.due === null ? `With ${owner} · no SLA` : `With ${owner} · due ${formatDate(state.due)}`;
    case "pending":
      return state.sla_days === null ? owner : `${owner} · ${state.sla_days} days`;
  }
}

function JourneyStep({ state }: { state: StageState }) {
  const kind = kindOf(state);
  return (
    <li className={`journey-step is-${kind}`} aria-current={kind === "late" || kind === "open" ? "step" : undefined}>
      <span className="journey-dot" aria-hidden="true" />
      <span className="journey-text">
        <span className="journey-label">{state.label}</span>
        <span className="journey-meta">{metaOf(state, kind)}</span>
      </span>
      {kind === "late" && state.days_over !== null ? (
        <span className="journey-over">+{daysOver(state.days_over)}d</span>
      ) : null}
    </li>
  );
}

/** The three acts nothing else in the application records. Recording one a
 * second time is somebody correcting its date, and the server upserts it. */
function RecordMilestone({ journey }: { journey: Journey }) {
  const record = useRecordMilestone(journey.site.id);
  const [kind, setKind] = useState(journey.milestone_kinds[0] ?? "");
  const [at, setAt] = useState("");
  const [reference, setReference] = useState("");

  function submit(event: FormEvent) {
    event.preventDefault();
    record.mutate(
      { kind, at, reference: reference.trim() || null, note: null },
      { onSuccess: () => setReference("") },
    );
  }

  return (
    <form className="milestone-box" onSubmit={submit} aria-labelledby="milestone-title">
      <strong id="milestone-title">Record a milestone</strong>
      <div className="milestone-fields">
        <select className="select" aria-label="Milestone" value={kind} onChange={(e) => setKind(e.target.value)}>
          {journey.milestone_kinds.map((k) => (
            <option key={k} value={k}>
              {milestoneLabel(k)}
            </option>
          ))}
        </select>
        <input
          className="input"
          type="date"
          aria-label="Date"
          value={at}
          onChange={(e) => setAt(e.target.value)}
          required
        />
        <input
          className="input milestone-ref"
          aria-label="Reference"
          placeholder="Reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
        <button type="submit" className="btn btn-primary" disabled={record.isPending || at === "" || kind === ""}>
          {record.isPending ? "Recording…" : "Record"}
        </button>
      </div>
      <span className="milestone-note">Recording one twice corrects its date.</span>
      {record.isError ? <p className="notice bad">The milestone was not recorded: {record.error.message}</p> : null}
      {journey.milestones.length > 0 ? (
        <ul className="milestone-list" aria-label="Recorded milestones">
          {journey.milestones.map((m) => (
            <li key={m.kind}>
              <strong>{milestoneLabel(m.kind)}</strong> {formatDate(m.at)}
              {m.reference ? ` · ${m.reference}` : ""}
              {m.note ? ` · ${m.note}` : ""}
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
}
